import { apiClient, apiInterface, authClient, AuthUser, RefreshTokenResponse } from './api-client';
import { AxiosResponse } from 'axios';
import { MainType, PartnerForm } from '@/shared/formPortalSchema';

// ==================== TYPE DEFINITIONS ====================

export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductDto {
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
}

export interface UpdateProductDto extends Partial<CreateProductDto> { }

export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  }
}

export interface PaginatedData<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface PaginatedHubRes {
  data: string;
  success: boolean;
  status: string;
  message: string;
}

export interface PortalForm {
  Id: number;
  FormId: number;
  FormCode: string;
  Version: number;
  FormPartnerType: number;
  FormName: string;
  FormType: string;
  FormPartnerName: string;
  SchemaJson: string | null;
  IsActive: boolean;
  Published: boolean;
}

export interface InterfacePayload {
  description: string;
  implementStatus?: boolean;
  input: string;
  name: string;
  output: string;  
  type: number;
  repositoryInterfaceId: number | null;
}

export interface IntExtInterfacePayload {
  description: string;
  name: string;
  repositoryInterfaceId: number | null;
}

export interface intExtPayload {
  ids: number[]
}

export interface InterfaceAction {
  Id: number;
  name: string;
  path: string | null;
  type: number;
  input: string;
  useFlag: boolean | null;
  dateTimeChanged: string;
  changedBy: string | null;
  output: string;
  addedBy: string | null;
  applicationID: number | null;
  description: string;
  displaySequence: number | null;
  internalInterfaceType: number | null;
  repositoryInterfaceId: number | null;
  status: boolean;
  implementStatus: boolean;
}

export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  role: string;
}

export type GetFormDefinitionsParams = {
  page: number;
  pageSize: number;
  formType: string;
  q?: string;
  formCode?: string | number;
  formName?: string;
  partnerType?: string;
  offerId?: string | null
};

export type GetInterfaceParams = {
  page: number;
  pageSize: number;
  intId?: string;
  intName?: string;
  intType: string | undefined;
};

export type GetIntExtInterfaceParams = {
  page: number;
  pageSize: number;
  InterfaceId?: string;
  InterfaceName?: string;
  InterfaceType: string | undefined;
};

export interface UpdateUserDto extends Partial<Omit<CreateUserDto, 'password'>> {
  password?: string;
}

export type GetPartnersParams = {
  pageNumber: number,
  pageSize?: number,
  partnerName?: string,
  partnerType?: string,
  partnerSubType?: string,
};

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface PartnerData {
  items: PartnerForm[],
  page_no: number,
  page_size: number,
  total: number,
  total_pages: number
}

export interface AuditLogEntry {
  timestamp: string;
  error_type: string;
  status_code: number;
  error_message: string;
  response_time_ms: number;
  [key: string]: unknown;
}

export interface AuditDebugInfo {
  audit_log_dir: string;
  audit_log_dir_absolute: string;
  audit_log_dir_exists: boolean;
  available_dates: string[];
  entries_after_filter: number;
  entries_before_filter: number;
  log_file_absolute_path: string;
  log_file_exists: boolean;
  log_file_path: string;
  target_date: string;
}

export interface AuditFilters {
  start_date: string | null;
  end_date: string | null;
  error_type: string | null;
  status_code: number | null;
}

export interface AuditData {
  date: string;
  debug: AuditDebugInfo;
  logs: AuditLogEntry[];
  filters: AuditFilters;
  limit: number;
  offset: number;
  page: number;
  total: number;
}

// ==================== BPRM AUTH SERVICES ====================

export const authService = {
  me: async (): Promise<AuthUser> => {
    const response = await authClient.get<AuthUser>('/auth/me');
    return response.data;
  },

  refresh: async (): Promise<RefreshTokenResponse> => {
    const response = await authClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.data;
  },

  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await authClient.post('/auth/logout');
    return response.data;
  },

  offerFormId: async (id: number, referenceFormId: number): Promise<{ success: boolean; message: string, data: any }> => {
    console.log("referenceFormId", referenceFormId)
    const response = await authClient.put(`/exchange-offers/${id}/reference-form`, { ReferenceFormId: referenceFormId });
    return response.data;
  },

  additionalOfferFormId: async (id: number, additionalOfferId: number, referenceFormId: number): Promise<{ success: boolean; message: string, data: any }> => {
    console.log("referenceFormId", referenceFormId)
    const response = await authClient.put(`/exchange-offers/${id}/additional-offers/${additionalOfferId}/reference-form`, { ReferenceFormId: referenceFormId });
    return response.data;
  },
};

// ==================== BPRM SERVICES ====================
export const partnerService = {
  getPartnersData: async ({ pageNumber, pageSize, partnerName, partnerType, partnerSubType, }: GetPartnersParams): Promise<ApiResponse<PartnerData>> => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
    });

    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }
    if (partnerName && partnerName.trim()) {
      params.append("partnerName", partnerName);
    }
    if (partnerType && partnerType.trim()) {
      params.append("partnerType", partnerType);
    }
    if (partnerSubType && partnerSubType) {
      params.append("partnerSubType", partnerSubType);
    }

    const response = await authClient.get(`/partnerforms?${params.toString()}&includeInactive=false`)
    return response.data;
  }
}

// ==================== All Interface Hub APIs ===============
export const interfaceRepo = {
  getInterfaceRepository: async ({
    page = 1,
    pageSize = 10,
    intId,
    intName,
    intType,
  }: GetInterfaceParams): Promise<PaginatedData<InterfaceAction>> => {
    const params = new URLSearchParams();

    params.append("page", String(page));
    params.append("page_size", String(pageSize));
    if (intId) params.append("id", intId);
    if (intName) params.append("name", intName);
    if (intType) params.append("type", intType);
    const response = await apiInterface.get<PaginatedData<InterfaceAction>>(
      `/api/interfaces/list?${params.toString()}`
    );
    return response.data;
  },

  createInterface: async (data: InterfacePayload): Promise<PaginatedHubRes> => {
    const response = await apiInterface.post<PaginatedHubRes>(`/api/interface/addInterface`, data);
    return response.data;
  },

  editInterface: async (Id: number, data: InterfacePayload): Promise<PaginatedHubRes> => {
    const response = await apiInterface.put<PaginatedHubRes>(`/api/interface/editInterface/${Id}`, data);
    return response.data;
  },

  deleteInterface: async (Id: number): Promise<InterfaceAction> => {
    const response = await apiInterface.delete<InterfaceAction>(`/api/interface/deleteInterface/${Id}`);
    return response.data;
  },

  statusToggleInterface: async (Id: number): Promise<InterfaceAction> => {
    const response = await apiInterface.put<InterfaceAction>(`/api/interface/toggleInterface/${Id}`);
    return response.data;
  },


  getIntExtInterface: async ({
    page = 1,
    pageSize = 10,
    InterfaceId,
    InterfaceName,
    InterfaceType,
  }: GetIntExtInterfaceParams): Promise<PaginatedData<InterfaceAction>> => {
    const params = new URLSearchParams();

    params.append("page", String(page));
    params.append("page_size", String(pageSize));
    if (InterfaceId) params.append("id", InterfaceId);
    if (InterfaceName) params.append("name", InterfaceName);
    // if (InterfaceType) params.append("interface_type", InterfaceType);
    const response = await apiInterface.get<PaginatedData<InterfaceAction>>(
      `/api/interface/list/${InterfaceType}?${params.toString()}`
    );
    return response.data;
  },

  getNonAddedIntExtInterface: async ({
    page = 1,
    pageSize = 10,
    InterfaceId,
    InterfaceName,
    InterfaceType,
  }: GetIntExtInterfaceParams): Promise<PaginatedData<InterfaceAction>> => {
    const params = new URLSearchParams();

    params.append("page", String(page));
    params.append("page_size", String(pageSize));
    if (InterfaceId) params.append("id", InterfaceId);
    if (InterfaceName) params.append("name", InterfaceName);
    const response = await apiInterface.get<PaginatedData<InterfaceAction>>(
      `/api/interface/hub-list/${InterfaceType}?${params.toString()}`
    );
    return response.data;
  },

  createIntExtInterface: async (data: intExtPayload): Promise<PaginatedHubRes> => {
    const response = await apiInterface.post<PaginatedHubRes>(`/api/interface`, data);
    return response.data;
  },

  editIntExtInterface: async (Id: number, data: IntExtInterfacePayload): Promise<PaginatedHubRes> => {
    const response = await apiInterface.put<PaginatedHubRes>(`/api/interface/${Id}`, data);
    return response.data;
  },

  deleteIntExtInterface: async (Id: number): Promise<InterfaceAction> => {
    const response = await apiInterface.delete<InterfaceAction>(`/api/interface/${Id}`);
    return response.data;
  },


}
// ==================== All Form SERVICES ====================

export const userService = {
  getAuditLog: async (filters?: { errorCode?: string; statusCode?: string; dateFrom?: string; dateTo?: string; pageSize?: string; pageIndex?: string; }): Promise<AuditData[]> => {
    const params = new URLSearchParams();

    params.set("limit", filters?.pageSize ?? "10");
    params.set("offset", filters?.pageIndex ?? "1");

    if (filters?.errorCode) {
      params.set("error_type", filters.errorCode);
    }

    if (filters?.statusCode) {
      params.set("status_code", filters.statusCode);
    }

    if (filters?.dateFrom) {
      params.set("start_date", filters.dateFrom);
    }

    if (filters?.dateTo) {
      params.set("end_date", filters.dateTo);
    }

    const response = await apiClient.get<AuditData[]>(`/api/audit?${params.toString()}`);
    return response.data;

  },
  
};